import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Colors, InputType } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services';
import { APP } from 'src/app/config/app';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { ColorComponent as ColorDetailComponent } from '../../../components/color/color.component';
@Component({
  selector: 'app-color',
  templateUrl: './color.component.html',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ColorDetailComponent],
  styleUrl: './color.component.scss',
})
export class ColorComponent implements OnChanges {
  @Input() type: InputType;
  @Input() value: any;
  @Input() values: any[];
  @Input() formControlName?: string;
  @Input() formGroup?: FormGroup;
  @Output() changeEventEmitter = new EventEmitter<unknown>();
  @ViewChild('customColorInput')
  customColorInput?: ElementRef<HTMLInputElement>;

  public Colors = Colors;
  public APP = APP;
  public customColorMode = false;
  public customColorValue = '';

  constructor(public literalService: LiteralService) {}

  ngOnChanges(): void {
    if (this.formControlName && this.formGroup) {
      this.formGroup!.setControl(
        this.formControlName!,
        new FormControl(this.value, [Validators.required]),
      );
      this.formGroup!.get(this.formControlName!)?.setValue(this.value);
      // Check if current value is a custom color (not in predefined values)
      if (this.value && this.values && !this.values.includes(this.value)) {
        this.customColorMode = true;
        this.customColorValue = this.value;
      }
    }
  }

  changeColor(event: any) {
    const selectedValue = event.target.value;
    if (selectedValue === 'CUSTOM') {
      this.customColorMode = true;
      this.customColorValue = '';
    } else {
      this.customColorMode = false;
      this.value = selectedValue;
      this.formControlName &&
        this.formGroup &&
        this.formGroup!.get(this.formControlName!)?.setValue(this.value);
      this.changeEventEmitter.emit(this.value);
    }
  }

  changeCustomColor(event: any) {
    const newValue = this.sanitizeHexColor(event.target.value);
    event.target.value = newValue;
    this.customColorValue = newValue;
    if (!APP.HEX_COLOR_PATTERN.test(newValue)) return;
    this.value = newValue;
    this.formControlName &&
      this.formGroup &&
      this.formGroup!.get(this.formControlName!)?.setValue(this.value);
    this.changeEventEmitter.emit(this.value);
    if (newValue.length < APP.HEX_COLOR_MAX_LENGTH)
      this.keepCustomColorFocused();
  }

  private keepCustomColorFocused() {
    setTimeout(() => {
      const input = this.customColorInput?.nativeElement;
      if (!input || document.activeElement === input) return;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  private sanitizeHexColor(rawValue: string): string {
    const digits = (rawValue ?? '')
      .replace(/[^A-Fa-f0-9]/g, '')
      .slice(0, APP.HEX_COLOR_MAX_LENGTH - 1);
    return `#${digits}`;
  }

  getValue() {
    if (!this.value) {
      return Colors.NONE;
    } else {
      return this.value;
    }
  }

  isCustomValue(): boolean {
    return this.value && this.values && !this.values.includes(this.value);
  }

  getColorLabel(color: string): string {
    // If it's a predefined color, get the translation
    if (APP.PREDEFINED_COLORS.has(color) || color === 'custom') {
      return this.literalService.get('colors.' + color, true);
    }
    // Custom color - just show the hex value
    return color;
  }
}
